import React, { useEffect, useRef, useState } from 'react';
import { theme } from 'antd';
import { ScanOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { useManagePermittedRoles } from 'library/hooks';
import { selectSites } from 'reduxStore/sites/selectors';
import { selectCardTemplatesIR } from 'reduxStore/types/selectors';
import { selectCurrentProfileOrgSettings } from 'reduxStore/settings/selectors';
import { CONSTANTS_SETTINGS } from 'config/settingsContainer';
import { Checkbox, Flex, Input, InputNumber, Select, Table, Typography } from 'zui/Antd';
import { SelectFontSizeLG } from 'zui/Antd/Select';

const { Text } = Typography;

const BatchIR = ({
	selectedTemplate,
	setSelectedTemplate,
	startCardNumber,
	setStartCardNumber,
	numberOfCards,
	setNumberOfCards,
	associateCSN,
	setAssociateCSN,
	csnMap,
	setCsnMap,
	selectedSiteIDs,
	setSelectedSiteIDs
}) => {
	const {
		token: {
			marginLG,
			marginXS,
			fontSizeSM,
			colorBorderSecondary,
			colorBgLayout,
			paddingSM,
			paddingXXS,
			colorPrimary,
			colorSuccess
		}
	} = theme.useToken();

	const sites = useSelector(selectSites);
	const cardTemplatesIR = useSelector(selectCardTemplatesIR);
	const orgSettings = useSelector(selectCurrentProfileOrgSettings);
	const isGlobalAdmin = useManagePermittedRoles('manageSiteAssignment');
	const isControlActive = Boolean(
		Number(orgSettings?.find((s) => s.defaultSettingID === CONSTANTS_SETTINGS.CONTROL_MODULE)?.value)
	);

	// Default template to site code 121 on mount
	useEffect(() => {
		if (!cardTemplatesIR?.length || selectedTemplate) return;
		const defaultTemplate = cardTemplatesIR.find(
			(t) => String(t.siteCode) === '121' || String(t.addressName) === '121'
		);
		if (defaultTemplate) setSelectedTemplate(defaultTemplate);
	}, [cardTemplatesIR, selectedTemplate, setSelectedTemplate]);

	const siteCode = selectedTemplate?.siteCode || selectedTemplate?.addressName || null;
	const showCards = startCardNumber?.length > 0 && numberOfCards > 0;

	// Increments a card number string preserving leading zeros (e.g. "01249" + 1 = "01250")
	const incrementCardNumber = (base, offset) => {
		const num = parseInt(base, 10) + offset;
		return String(num).padStart(base.length, '0');
	};
	const PAGE_SIZE = 7;
	const [activeRowIndex, setActiveRowIndex] = useState(0);
	const [currentPage, setCurrentPage] = useState(1);
	const tableRef = useRef(null);

	// Card reader — global keydown listener for CSN auto-fill
	// Only activates when no input is focused (reader fires rapid keystrokes globally).
	// When the CSN Input is focused, the user is typing manually — let the Input handle it.
	useEffect(() => {
		if (!associateCSN || !showCards) return;

		let buffer = '';
		let timeout;

		const handleKeyDown = (e) => {
			const tag = e.target.tagName;
			if (tag === 'INPUT' || tag === 'TEXTAREA') return;

			clearTimeout(timeout);

			if (e.key === 'Enter') {
				if (buffer.length > 4) {
					setCsnMap((prev) => ({ ...prev, [activeRowIndex]: buffer }));
					setActiveRowIndex((prev) => Math.min(prev + 1, numberOfCards - 1));
				}
				buffer = '';
				return;
			}

			if (e.key.length === 1) buffer += e.key;

			timeout = setTimeout(() => {
				buffer = '';
			}, 50);
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			clearTimeout(timeout);
		};
	}, [associateCSN, showCards, activeRowIndex, numberOfCards, setCsnMap]);

	// Reset active row when toggling or changing card count
	useEffect(() => {
		setActiveRowIndex(0);
		setCurrentPage(1);
	}, [associateCSN, startCardNumber, numberOfCards]);

	// Auto-navigate pagination when reader advances to next row
	useEffect(() => {
		setCurrentPage(Math.floor(activeRowIndex / PAGE_SIZE) + 1);
	}, [activeRowIndex]);

	// Build table data
	const tableData = showCards
		? Array.from({ length: numberOfCards }, (_, i) => ({
				key: i,
				cardNumber: incrementCardNumber(startCardNumber, i),
				cardName: `Card ${incrementCardNumber(startCardNumber, i)}`,
				csn: csnMap[i] || null
			}))
		: [];

	const scannedCount = Object.keys(csnMap).length;

	const columns = [
		{
			title: '#',
			dataIndex: 'cardNumber',
			key: 'cardNumber',
			width: 80,
			render: (val) => <Text style={{ fontSize: fontSizeSM }}>{val}</Text>
		},
		{
			title: 'Card Name',
			dataIndex: 'cardName',
			key: 'cardName',
			width: 130,
			render: (val) => <Text style={{ fontSize: fontSizeSM }}>{val}</Text>
		},
		{
			title: 'CSN',
			dataIndex: 'csn',
			key: 'csn',
			render: (csn, record) => {
				const rowIndex = record.key;
				const isActive = rowIndex === activeRowIndex;

				if (csn && !isActive) {
					return (
						<Flex
							align='center'
							gap={6}
							style={{ cursor: 'pointer' }}
							onClick={() => setActiveRowIndex(rowIndex)}
						>
							<CheckCircleOutlined style={{ color: colorSuccess, fontSize: 12 }} />
							<Text code style={{ fontSize: 11 }}>
								{csn}
							</Text>
						</Flex>
					);
				}

				if (isActive) {
					return (
						<Input
							size='small'
							value={csn || ''}
							onChange={(e) => {
								const val = e.target.value;
								setCsnMap((prev) => {
									const next = { ...prev };
									if (val) next[rowIndex] = val;
									else delete next[rowIndex];
									return next;
								});
							}}
							onPressEnter={() => {
								if (csn) setActiveRowIndex(Math.min(rowIndex + 1, numberOfCards - 1));
							}}
							placeholder='Tap or type CSN...'
							prefix={<ScanOutlined style={{ color: colorPrimary, fontSize: 11 }} />}
							autoFocus
							style={{ fontSize: 11 }}
						/>
					);
				}

				return (
					<Text
						type='secondary'
						style={{ fontSize: 11, cursor: 'pointer' }}
						onClick={() => setActiveRowIndex(rowIndex)}
					>
						—
					</Text>
				);
			}
		}
	];

	return (
		<Flex gap={marginLG} style={{ minHeight: 0 }}>
			{/* Left: Configuration */}
			<Flex vertical gap={marginLG} style={{ flex: 1, minWidth: 0 }}>
				{/* Site Selection */}
				{sites.length > 0 && (
					<Flex vertical gap={marginXS}>
						<Text strong>
							Select Site {!isGlobalAdmin && <Text type='danger'>*</Text>}
						</Text>
						<Text type='secondary' style={{ fontSize: fontSizeSM }}>
							{isGlobalAdmin
								? 'Select the sites to assign these cards to.'
								: 'Cards will be assigned to the selected site.'}
						</Text>
						{isGlobalAdmin ? (
							<SelectFontSizeLG
								mode='multiple'
								value={selectedSiteIDs}
								onChange={setSelectedSiteIDs}
								placeholder='Select Sites'
								options={sites.map((site) => ({
									label: site.name,
									value: site.siteID
								}))}
								showSearch={false}
								virtual={false}
								maxTagCount={2}
								maxTagPlaceholder={(omittedValues) => `+${omittedValues.length}`}
								$tagColor={colorPrimary}
								style={{ width: '100%' }}
								allowClear
							/>
						) : (
							<Select
								value={selectedSiteIDs[0]}
								disabled={sites.length <= 1}
								onChange={(value) => setSelectedSiteIDs([value])}
								options={sites.map((site) => ({
									label: site.name,
									value: site.siteID
								}))}
								style={{ width: '100%' }}
							/>
						)}
						{!isGlobalAdmin && sites.length <= 1 && (
							<Text type='secondary' style={{ fontSize: fontSizeSM }}>
								Cards will automatically be added to your assigned site.
							</Text>
						)}
					</Flex>
				)}

				<Flex vertical gap={marginXS}>
					<Text strong>
						Template <Text type='danger'>*</Text>
					</Text>
					<Text type='secondary' style={{ fontSize: fontSizeSM }}>
						Select the card template for all cards in this batch.
					</Text>
					<Select
						showSearch
						placeholder='Select a template...'
						value={selectedTemplate?.credentialTemplateID || undefined}
						onChange={(val) =>
							setSelectedTemplate(cardTemplatesIR.find((t) => t.credentialTemplateID === val))
						}
						style={{ width: '100%' }}
						options={cardTemplatesIR?.map((t) => ({
							label: `${t.name} (${t.addressName || t.siteCode})`,
							value: t.credentialTemplateID
						}))}
					/>
					{siteCode && (
						<Flex
							style={{
								background: colorBgLayout,
								padding: `${paddingSM}px`,
								borderRadius: 8,
								border: `1px solid ${colorBorderSecondary}`
							}}
						>
							<Text type='secondary' style={{ fontSize: fontSizeSM }}>
								Site Code: <Text strong>{siteCode}</Text>
							</Text>
						</Flex>
					)}
				</Flex>

				<Flex gap={marginLG}>
					<Flex vertical gap={marginXS} style={{ flex: 1 }}>
						<Text strong>
							Start at card number <Text type='danger'>*</Text>
						</Text>
						<Input
							value={startCardNumber}
							onChange={(e) => {
								const val = e.target.value.replace(/[^0-9]/g, '');
								if (val.length <= 8) setStartCardNumber(val);
							}}
							maxLength={8}
							placeholder='e.g. 01250'
							style={{ width: '100%' }}
						/>
					</Flex>

					<Flex vertical gap={marginXS} style={{ flex: 1 }}>
						<Text strong>
							Number of cards <Text type='danger'>*</Text>
						</Text>
						<InputNumber
							min={1}
							max={1000}
							value={numberOfCards}
							onChange={setNumberOfCards}
							placeholder='e.g. 100'
							style={{ width: '100%' }}
						/>
					</Flex>
				</Flex>

				{showCards && (
					<>
						<Flex
							vertical
							gap={4}
							style={{
								background: colorBgLayout,
								padding: paddingSM,
								borderRadius: 8,
								border: `1px solid ${colorBorderSecondary}`
							}}
						>
							<Text strong style={{ fontSize: fontSizeSM }}>
								Preview
							</Text>
							<Text type='secondary' style={{ fontSize: fontSizeSM }}>
								{numberOfCards} card{numberOfCards > 1 ? 's' : ''} will be created: Card{' '}
								{startCardNumber} to Card {incrementCardNumber(startCardNumber, numberOfCards - 1)}{' '}
								(Site Code: {siteCode})
							</Text>
						</Flex>

						{isControlActive && (
							<Checkbox checked={associateCSN} onChange={(e) => setAssociateCSN(e.target.checked)}>
								<Text strong style={{ fontSize: fontSizeSM }}>
									Associate cards now
								</Text>
							</Checkbox>
						)}
					</>
				)}
			</Flex>

			{/* Right: CSN scanning table */}
			{associateCSN && showCards && (
				<Flex
					vertical
					gap={paddingSM}
					ref={tableRef}
					style={{
						flex: 1,
						minWidth: 320,
						maxWidth: 400,
						borderLeft: `1px solid ${colorBorderSecondary}`,
						paddingLeft: marginLG
					}}
				>
					<Flex vertical gap={paddingXXS}>
						<Flex justify='space-between' align='center'>
							<Flex align='center' gap={6}>
								<ScanOutlined style={{ color: colorPrimary, fontSize: 14 }} />
								<Text strong style={{ fontSize: fontSizeSM }}>
									Card Scanner
								</Text>
							</Flex>
							<Text type='secondary' style={{ fontSize: 11 }}>
								{scannedCount} / {numberOfCards} scanned
							</Text>
						</Flex>
						<Text type='secondary' style={{ fontSize: 11 }}>
							Link each card's CSN so permissions propagate to your digital devices (Control).
						</Text>
					</Flex>

					<Table
						columns={columns}
						dataSource={tableData}
						pagination={{
							pageSize: PAGE_SIZE,
							current: currentPage,
							onChange: setCurrentPage,
							showSizeChanger: false,
							size: 'small',
							simple: true
						}}
						size='small'
						onRow={(record) => ({ onClick: () => setActiveRowIndex(record.key) })}
						rowClassName={(record) => (record.key === activeRowIndex ? 'ant-table-row-selected' : '')}
					/>
				</Flex>
			)}
		</Flex>
	);
};

export default BatchIR;
